import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";

const EMPTY = { call_date: "", call_time: "", caller_name: "", caller_phone: "", regarding: "", message: "", callback_needed: true };

function todayISO() { return new Date().toISOString().slice(0, 10); }
function nowTime() { return new Date().toTimeString().slice(0, 5); }

export default function MissedCallsPage() {
  const { user: me } = useAuth();
  const [calls, setCalls] = useState(null);
  const [openCbs, setOpenCbs] = useState(0);
  const [f, setF] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/missed-calls").then((r) => { setCalls(r.data.calls); setOpenCbs(r.data.open_callbacks); })
      .catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setF((c) => ({ ...c, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async () => {
    if (!f.caller_name.trim()) { toast.error("Who called is required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/missed-calls", f);
      toast.success("Call logged");
      setF(EMPTY);
      setShowForm(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const markDone = async (c) => {
    try {
      await api.put(`/admin/missed-calls/${c.id}`, { callback_done: true });
      toast.success("Callback marked returned");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const printLog = async () => {
    const w = window.open("", "_blank");
    try {
      const r = await api.get("/admin/missed-calls/print.pdf", { responseType: "blob" });
      if (w) w.location.href = URL.createObjectURL(r.data);
    } catch (e) { if (w) w.close(); toast.error(formatApiError(e)); }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-5 px-4 py-8" data-testid="missed-calls-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900" data-testid="missed-calls-title">Missed calls log</h1>
          <p className="mt-1 max-w-2xl text-sm text-stone-500">
            Every missed or after-hours call: who called, who took the message, and whether we called back.
            {openCbs > 0 && <span className="font-semibold text-[#C0392B]"> {openCbs} callback{openCbs === 1 ? "" : "s"} still owed.</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { setF({ ...EMPTY, call_date: todayISO(), call_time: nowTime() }); setShowForm((v) => !v); }}
            className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="missed-call-add">
            {showForm ? "Cancel" : "Log a missed call"}
          </Button>
          <Button variant="outline" onClick={printLog} className="rounded-full" data-testid="missed-calls-print">Print log</Button>
        </div>
      </div>

      {showForm && (
        <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="missed-call-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-500">Who called *</label>
              <Input value={f.caller_name} onChange={set("caller_name")} className="h-9 border-stone-300 text-sm" data-testid="missed-call-caller" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-500">Their phone</label>
              <Input value={f.caller_phone} onChange={set("caller_phone")} className="h-9 border-stone-300 text-sm" data-testid="missed-call-phone" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-500">Date</label>
              <Input type="date" value={f.call_date} onChange={set("call_date")} className="h-9 border-stone-300 text-sm" data-testid="missed-call-date" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-500">Time</label>
              <Input type="time" value={f.call_time} onChange={set("call_time")} className="h-9 border-stone-300 text-sm" data-testid="missed-call-time" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-500">Regarding (client or topic)</label>
              <Input value={f.regarding} onChange={set("regarding")} className="h-9 border-stone-300 text-sm" data-testid="missed-call-regarding" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-stone-500">Taken by</label>
              <Input value={f.taken_by ?? (me?.name || "")} onChange={set("taken_by")} placeholder={me?.name || "office"} className="h-9 border-stone-300 text-sm" data-testid="missed-call-takenby" />
            </div>
          </div>
          <div className="mt-3">
            <label className="mb-1 block text-xs font-medium text-stone-500">Message</label>
            <Input value={f.message} onChange={set("message")} placeholder="What they called about" className="h-9 border-stone-300 text-sm" data-testid="missed-call-message" />
          </div>
          <label className="mt-3 flex items-center gap-2 text-sm text-stone-700" data-testid="missed-call-callback-wrap">
            <input type="checkbox" checked={f.callback_needed} onChange={set("callback_needed")} data-testid="missed-call-callback" />
            We need to call them back
          </label>
          <Button onClick={save} disabled={busy} className="mt-3 bg-primary text-primary-foreground hover:bg-stone-900" data-testid="missed-call-save">
            {busy ? "Saving…" : "Log the call"}
          </Button>
        </div>
      )}

      <div className="space-y-2" data-testid="missed-calls-list">
        {calls === null ? (
          <p className="text-sm text-stone-400">Loading…</p>
        ) : calls.length === 0 ? (
          <div className="rounded-xl border border-dashed border-stone-300 bg-white px-6 py-10 text-center" data-testid="missed-calls-empty">
            <p className="text-sm font-semibold text-stone-600">No missed calls logged</p>
            <p className="mt-1 text-xs text-stone-400">Use "Log a missed call" whenever the office misses one.</p>
          </div>
        ) : (
          calls.map((c) => (
            <div key={c.id} className="rounded-lg border border-stone-200 bg-white px-4 py-3" data-testid={`missed-call-row-${c.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-stone-500">{c.call_date}{c.call_time ? ` ${c.call_time}` : ""}</span>
                <span className="text-sm font-semibold text-stone-900">{c.caller_name}</span>
                {c.caller_phone && <a href={`tel:${c.caller_phone}`} className="text-xs text-primary underline" data-testid={`missed-call-phone-link-${c.id}`}>{c.caller_phone}</a>}
                {c.regarding && <span className="text-xs text-stone-500">re: {c.regarding}</span>}
                <span className="ml-auto text-[10px] text-stone-400">taken by {c.taken_by}</span>
                {c.callback_needed && !c.callback_done && (
                  <span className="rounded-full border border-[#C0392B]/30 bg-[#C0392B]/10 px-2 py-0.5 text-[10px] font-bold text-[#C0392B]" data-testid={`missed-call-owed-${c.id}`}>CALLBACK OWED</span>
                )}
                {c.callback_needed && c.callback_done && (
                  <span className="rounded-full border border-[#27AE60]/30 bg-[#27AE60]/10 px-2 py-0.5 text-[10px] font-bold text-[#1e8449]" data-testid={`missed-call-returned-${c.id}`}>called back ✓</span>
                )}
              </div>
              {c.message && <p className="mt-1 text-xs text-stone-600">{c.message}</p>}
              {c.callback_needed && !c.callback_done && (
                <Button size="sm" variant="outline" onClick={() => markDone(c)} className="mt-2 rounded-full" data-testid={`missed-call-done-${c.id}`}>
                  Mark callback returned
                </Button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
